/**
 * Weak Spots Block - Widget 5
 * 
 * Identifies letters/areas needing improvement with actionable recommendations
 * Shows priority levels, improvement potential, and estimated practice time
 */

import React, { useState, useMemo } from 'react';
import { WeakSpotData } from '@/types/statistics';

interface WeakSpotsBlockProps {
  data: WeakSpotData[];
  loading: boolean;
}

type FilterOption = 'all' | 'high' | 'medium' | 'low';
type SortOption = 'priority' | 'potential' | 'accuracy' | 'speed' | 'practice';

const WeakSpotsBlock: React.FC<WeakSpotsBlockProps> = ({ 
  data, 
  loading 
}) => {
  const [filterPriority, setFilterPriority] = useState<FilterOption>('all');
  const [sortBy, setSortBy] = useState<SortOption>('priority');
  const [showRecommendations, setShowRecommendations] = useState(true);

  // Process and filter data
  const processedData = useMemo(() => {
    let filtered = [...data];

    // Apply priority filter
    if (filterPriority !== 'all') {
      filtered = filtered.filter(spot => spot.priority === filterPriority);
    }

    // Sort data
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'priority':
          const priorityOrder = { 'high': 3, 'medium': 2, 'low': 1 };
          return priorityOrder[b.priority] - priorityOrder[a.priority];
        
        case 'potential':
          return b.improvementPotential - a.improvementPotential;
        
        case 'accuracy':
          return a.accuracy - b.accuracy; // Lower accuracy first
        
        case 'speed':
          return b.avgSpeed - a.avgSpeed; // Slower speed first
        
        case 'practice':
          return a.estimatedPracticeTime - b.estimatedPracticeTime; // Less time first
        
        default:
          return 0;
      }
    });

    return filtered;
  }, [data, filterPriority, sortBy]);

  // Calculate summary statistics
  const summaryStats = useMemo(() => {
    const highPriority = data.filter(spot => spot.priority === 'high').length;
    const totalPracticeTime = data.reduce((sum, spot) => sum + spot.estimatedPracticeTime, 0);
    const avgAccuracy = data.length > 0 
      ? data.reduce((sum, spot) => sum + spot.accuracy, 0) / data.length 
      : 0;
    const avgPotential = data.length > 0
      ? data.reduce((sum, spot) => sum + spot.improvementPotential, 0) / data.length
      : 0;

    return {
      totalSpots: data.length,
      highPriority,
      totalPracticeTime,
      avgAccuracy,
      avgPotential
    };
  }, [data]);

  if (loading) {
    return <WeakSpotsSkeletonBlock />;
  }

  if (data.length === 0) {
    return <EmptyWeakSpotsBlock />;
  }

  return (
    <div style={{ background: '#1e293b', borderRadius: '8px', border: '1px solid #334155', padding: '16px', marginBottom: '16px' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px', flexWrap: 'wrap', gap: '12px' }}>
        <div>
          <h2 style={{ fontSize: '16px', fontWeight: '600', color: '#ffffff', margin: 0, display: 'flex', alignItems: 'center', gap: '6px' }}>
            🎯 Areas for Improvement
          </h2>
          <p style={{ fontSize: '11px', color: '#94a3b8', marginTop: '2px', margin: 0 }}>
            Focus areas to maximize your progress • {data.length} areas identified
          </p>
        </div>

        {/* Controls */}
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          {/* Priority Filter */}
          <select
            value={filterPriority}
            onChange={(e) => setFilterPriority(e.target.value as FilterOption)}
            style={{ padding: '6px 8px', borderRadius: '6px', border: '1px solid #475569', background: '#334155', color: '#e2e8f0', fontSize: '11px' }}
          >
            <option value="all">All Priorities ({data.length})</option>
            <option value="high">High Priority ({data.filter(s => s.priority === 'high').length})</option>
            <option value="medium">Medium Priority ({data.filter(s => s.priority === 'medium').length})</option>
            <option value="low">Low Priority ({data.filter(s => s.priority === 'low').length})</option>
          </select>

          {/* Sort Option */}
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value as SortOption)}
            style={{ padding: '6px 8px', borderRadius: '6px', border: '1px solid #475569', background: '#334155', color: '#e2e8f0', fontSize: '11px' }}
          >
            <option value="priority">Sort by Priority</option>
            <option value="potential">Sort by Potential</option>
            <option value="accuracy">Sort by Accuracy</option>
            <option value="speed">Sort by Speed</option>
            <option value="practice">Sort by Practice Time</option>
          </select>
        </div>
      </div>

      {/* Summary Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(100px, 1fr))', gap: '8px', marginBottom: '16px' }}>
        <SummaryCard
          icon="⚠️"
          label="High Priority"
          value={summaryStats.highPriority}
          subtitle="need attention"
          color="#ef4444"
        />
        <SummaryCard
          icon="⏱️"
          label="Est. Practice"
          value={`${summaryStats.totalPracticeTime}m`}
          subtitle="to improve"
          color="#3b82f6"
        />
        <SummaryCard
          icon="📊"
          label="Avg Accuracy"
          value={`${summaryStats.avgAccuracy.toFixed(1)}%`}
          subtitle="across weak areas"
          color="#f59e0b"
        />
        <SummaryCard
          icon="🎯"
          label="Avg Potential"
          value={`${summaryStats.avgPotential.toFixed(0)}%`}
          subtitle="improvement"
          color="#16a34a"
        />
      </div>

      {/* Toggle Recommendations */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
        <h3 style={{ fontSize: '14px', fontWeight: '500', color: '#e2e8f0' }}>Identified Issues</h3>
        <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', color: '#e2e8f0' }}>
          <input
            type="checkbox"
            checked={showRecommendations}
            onChange={(e) => setShowRecommendations(e.target.checked)}
            style={{ borderRadius: '3px' }}
          />
          <span>Show Recommendations</span>
        </label>
      </div>

      {/* Weak Spots List */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
        {processedData.length > 0 ? (
          processedData.map((spot, index) => (
            <WeakSpotCard
              key={spot.letter}
              spot={spot}
              index={index}
              showRecommendations={showRecommendations}
            />
          ))
        ) : (
          <div style={{ textAlign: 'center', padding: '32px 0', color: '#94a3b8' }}>
            <div style={{ fontSize: '32px', marginBottom: '8px' }}>🎉</div>
            <div style={{ fontWeight: '500' }}>No {filterPriority} priority issues found!</div>
            <div style={{ fontSize: '11px' }}>Try adjusting the filter to see other areas.</div>
          </div>
        )}
      </div>

      {/* Action Plan */}
      {processedData.length > 0 && (
        <div style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1px solid #475569' }}>
          <ActionPlan data={processedData.slice(0, 3)} />
        </div>
      )}
    </div>
  );
};

// Individual Weak Spot Card Component
const WeakSpotCard: React.FC<{
  spot: WeakSpotData;
  index: number;
  showRecommendations: boolean;
}> = ({ spot, index, showRecommendations }) => {

  const getPriorityStyle = (priority: string) => {
    if (priority === 'high') return { background: '#7f1d1d' };
    if (priority === 'medium') return { background: '#92400e' };
    return { background: '#1e40af' };
  };

  const getPriorityBorderColor = (priority: string) => {
    if (priority === 'high') return '#dc2626';
    if (priority === 'medium') return '#f59e0b';
    return '#3b82f6';
  };

  const getPriorityBadgeStyle = (priority: string) => {
    if (priority === 'high') return { background: '#dc2626', color: '#fee2e2' };
    if (priority === 'medium') return { background: '#f59e0b', color: '#fef3c7' };
    return { background: '#3b82f6', color: '#dbeafe' };
  };

  const getPriorityIcon = (priority: string) => {
    if (priority === 'high') return '🚨';
    if (priority === 'medium') return '⚠️';
    return '💡';
  };

  const getAccuracyColorStyle = (accuracy: number) => {
    if (accuracy >= 90) return { color: '#ca8a04' };
    if (accuracy >= 80) return { color: '#ea580c' };
    return { color: '#dc2626' };
  };

  const getSpeedColorStyle = (speed: number) => {
    if (speed <= 200) return { color: '#16a34a' };
    if (speed <= 350) return { color: '#ca8a04' };
    return { color: '#dc2626' };
  };

  return (
    <div style={{
      ...getPriorityStyle(spot.priority),
      borderRadius: '6px',
      padding: '12px',
      border: '1px solid',
      borderColor: getPriorityBorderColor(spot.priority)
    }}>
      <div style={{ display: 'flex', alignItems: 'start', justifyContent: 'space-between' }}>
        {/* Left side - Letter and stats */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          {/* Rank number */}
          <div style={{ flexShrink: '0' }}>
            <div style={{
              width: '24px',
              height: '24px',
              borderRadius: '50%',
              ...getPriorityBadgeStyle(spot.priority),
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '11px',
              fontWeight: '700'
            }}>
              {index + 1}
            </div>
          </div>

          {/* Letter */}
          <div style={{ flexShrink: '0' }}>
            <div style={{ fontFamily: 'monospace', fontSize: '24px', fontWeight: '700', color: '#1e293b', background: '#ffffff', padding: '8px 10px', borderRadius: '6px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
              {spot.letter}
            </div>
          </div>

          {/* Stats */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <span style={{ fontSize: '11px', color: '#94a3b8' }}>Accuracy:</span>
                <span style={{ fontWeight: '700', fontSize: '12px', ...getAccuracyColorStyle(spot.accuracy) }}>
                  {spot.accuracy.toFixed(1)}%
                </span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <span style={{ fontSize: '11px', color: '#94a3b8' }}>Speed:</span>
                <span style={{ fontWeight: '700', fontSize: '12px', ...getSpeedColorStyle(spot.avgSpeed) }}>
                  {spot.avgSpeed.toFixed(0)}ms
                </span>
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <span style={{ fontSize: '11px', color: '#94a3b8' }}>Practice:</span>
                <span style={{ fontWeight: '500', color: '#e2e8f0', fontSize: '11px' }}>
                  {spot.practiceCount.toLocaleString()}
                </span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <span style={{ fontSize: '11px', color: '#94a3b8' }}>Est. Time:</span>
                <span style={{ fontWeight: '500', color: '#3b82f6', fontSize: '11px' }}>
                  {spot.estimatedPracticeTime}min
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Right side - Priority and potential */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'end', gap: '8px' }}>
          {/* Priority Badge */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            padding: '4px 8px',
            borderRadius: '12px',
            fontSize: '10px',
            fontWeight: '500',
            ...getPriorityBadgeStyle(spot.priority)
          }}>
            <span>{getPriorityIcon(spot.priority)}</span>
            <span style={{ textTransform: 'capitalize' }}>{spot.priority} Priority</span>
          </div>

          {/* Improvement Potential */}
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: '10px', color: '#94a3b8', marginBottom: '4px' }}>Improvement Potential</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <div style={{ width: '60px', background: '#475569', borderRadius: '12px', height: '6px' }}>
                <div 
                  style={{ 
                    background: '#16a34a', 
                    height: '6px', 
                    borderRadius: '12px', 
                    width: `${spot.improvementPotential}%`,
                    transition: 'width 0.3s ease'
                  }}
                />
              </div>
              <span style={{ fontSize: '10px', fontWeight: '700', color: '#16a34a' }}>
                {spot.improvementPotential}%
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Recommendation */}
      {showRecommendations && (
        <div style={{ marginTop: '12px', paddingTop: '12px', borderTop: '1px solid #475569' }}>
          <div style={{ display: 'flex', alignItems: 'start', gap: '8px' }}>
            <div style={{ flexShrink: '0', fontSize: '14px' }}>💡</div>
            <div>
              <h4 style={{ fontWeight: '500', color: '#e2e8f0', marginBottom: '4px', fontSize: '11px' }}>Recommendation</h4>
              <p style={{ fontSize: '11px', color: '#94a3b8' }}>{spot.recommendation}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Summary Card Component
const SummaryCard: React.FC<{
  icon: string;
  label: string;
  value: string | number;
  subtitle: string;
  color: string;
}> = ({ icon, label, value, subtitle, color }) => (
  <div style={{ background: '#334155', borderRadius: '6px', padding: '12px', textAlign: 'center' }}>
    <div style={{ fontSize: '16px', marginBottom: '6px' }}>{icon}</div>
    <div style={{ fontSize: '9px', color: '#94a3b8', marginBottom: '4px', fontWeight: '500', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
      {label}
    </div>
    <div style={{ fontSize: '14px', fontWeight: '700', color, marginBottom: '2px' }}>
      {value}
    </div>
    <div style={{ fontSize: '9px', color: '#64748b' }}>
      {subtitle}
    </div>
  </div>
);

// Action Plan Component
const ActionPlan: React.FC<{ data: WeakSpotData[] }> = ({ data }) => {
  const totalTime = data.reduce((sum, spot) => sum + spot.estimatedPracticeTime, 0);

  return (
    <div>
      <h3 style={{ fontSize: '14px', fontWeight: '500', color: '#e2e8f0', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '6px' }}>
        📋 Recommended Action Plan
      </h3>
      
      <div style={{ background: '#1e40af', borderRadius: '6px', padding: '12px', border: '1px solid #3b82f6' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
          <div style={{ fontSize: '16px' }}>🎯</div>
          <div>
            <h4 style={{ fontWeight: '500', color: '#dbeafe', fontSize: '12px' }}>Focus on Top 3 Areas</h4>
            <p style={{ fontSize: '10px', color: '#bfdbfe' }}>
              Estimated practice time: {totalTime} minutes
            </p>
          </div>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {data.map((spot, index) => (
            <div key={spot.letter} style={{ display: 'flex', alignItems: 'center', gap: '8px', background: '#ffffff', borderRadius: '6px', padding: '8px' }}>
              <div style={{ flexShrink: '0', width: '20px', height: '20px', background: '#3b82f6', color: '#ffffff', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '10px', fontWeight: '700' }}>
                {index + 1}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '2px' }}>
                  <span style={{ fontFamily: 'monospace', fontSize: '14px', fontWeight: '700' }}>{spot.letter}</span>
                  <span style={{ fontSize: '10px', color: '#64748b' }}>
                    ({spot.accuracy.toFixed(1)}% accuracy, {spot.avgSpeed.toFixed(0)}ms speed)
                  </span>
                </div>
                <div style={{ fontSize: '10px', color: '#374151' }}>
                  {spot.recommendation}
                </div>
              </div>
              <div style={{ flexShrink: '0', fontSize: '10px', fontWeight: '500', color: '#3b82f6' }}>
                {spot.estimatedPracticeTime}min
              </div>
            </div>
          ))}
        </div>

        <div style={{ marginTop: '10px', padding: '8px', background: '#1e40af', borderRadius: '6px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '10px', color: '#dbeafe' }}>
            <span>💡</span>
            <span style={{ fontWeight: '500' }}>Pro Tip:</span>
            <span>
              Practice these letters in short, focused sessions for better results.
              {totalTime <= 15 
                ? " You can complete all three in one session!" 
                : " Consider spreading this across multiple sessions."
              }
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

// Loading Skeleton
const WeakSpotsSkeletonBlock: React.FC = () => (
  <div style={{ background: '#1e293b', borderRadius: '8px', border: '1px solid #334155', padding: '16px', marginBottom: '16px' }}>
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
      <div>
        <div style={{ height: '16px', background: '#475569', borderRadius: '4px', width: '180px', marginBottom: '8px' }}></div>
        <div style={{ height: '12px', background: '#475569', borderRadius: '4px', width: '200px' }}></div>
      </div>
      <div style={{ display: 'flex', gap: '8px' }}>
        <div style={{ height: '32px', background: '#475569', borderRadius: '6px', width: '120px' }}></div>
        <div style={{ height: '32px', background: '#475569', borderRadius: '6px', width: '120px' }}></div>
      </div>
    </div>
    
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(100px, 1fr))', gap: '8px', marginBottom: '16px' }}>
      {Array.from({ length: 4 }).map((_, i) => (
        <div key={i} style={{ background: '#334155', borderRadius: '6px', padding: '12px', textAlign: 'center' }}>
          <div style={{ height: '16px', background: '#475569', borderRadius: '4px', marginBottom: '6px', margin: '0 auto', width: '16px' }}></div>
          <div style={{ height: '9px', background: '#475569', borderRadius: '4px', width: '48px', marginBottom: '4px', margin: '0 auto' }}></div>
          <div style={{ height: '14px', background: '#475569', borderRadius: '4px', width: '36px', marginBottom: '2px', margin: '0 auto' }}></div>
          <div style={{ height: '9px', background: '#475569', borderRadius: '4px', width: '60px', margin: '0 auto' }}></div>
        </div>
      ))}
    </div>
    
    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
      {Array.from({ length: 4 }).map((_, i) => (
        <div key={i} style={{ background: '#7f1d1d', borderRadius: '6px', padding: '12px', border: '1px solid #dc2626' }}>
          <div style={{ display: 'flex', alignItems: 'start', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div style={{ height: '24px', background: '#dc2626', borderRadius: '50%', width: '24px' }}></div>
              <div style={{ height: '24px', background: '#ffffff', borderRadius: '6px', width: '24px' }}></div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                <div style={{ height: '12px', background: '#475569', borderRadius: '4px', width: '96px' }}></div>
                <div style={{ height: '12px', background: '#475569', borderRadius: '4px', width: '120px' }}></div>
              </div>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <div style={{ height: '20px', background: '#dc2626', borderRadius: '12px', width: '80px' }}></div>
              <div style={{ height: '10px', background: '#475569', borderRadius: '4px', width: '60px' }}></div>
            </div>
          </div>
        </div>
      ))}
    </div>
  </div>
);

// Empty State
const EmptyWeakSpotsBlock: React.FC = () => (
  <div style={{ background: '#1e293b', borderRadius: '8px', border: '1px solid #334155', padding: '16px', marginBottom: '16px' }}>
    <div style={{ textAlign: 'center', padding: '48px 0' }}>
      <div style={{ fontSize: '48px', marginBottom: '16px' }}>🎉</div>
      <h3 style={{ fontSize: '16px', fontWeight: '500', color: '#ffffff', marginBottom: '8px' }}>
        No Weak Spots Found!
      </h3>
      <p style={{ color: '#94a3b8', marginBottom: '16px', fontSize: '14px' }}>
        Great job! You don't have any significant areas needing improvement.
      </p>
      <div style={{ fontSize: '12px', color: '#64748b' }}>
        Keep practicing to maintain your excellent performance! 🚀
      </div>
    </div>
  </div>
);

export default WeakSpotsBlock;